/**
 * Root container for the Settings visual editor (TASK-181 WP-D).
 *
 * Hosts the WYSIWYG layout:
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ TabListEditor — horizontal strip, full pane width            │
 *   ├────────────────────────────┬────────────────────────────────┤
 *   │ FieldListEditor            │ FieldPropertyPanel             │
 *   │ (fields in active tab)     │ (selected field's properties)  │
 *   └────────────────────────────┴────────────────────────────────┘
 *   PaneFooter: Save / Reset / mode toggle (mirrors TASK-180 raw mode).
 *
 * State: `useReducer(editorReducer)` for the in-memory FormConfig.
 * Data layer: same hooks as TASK-180 — `useModuleSetting` (seed),
 * `useSaveModuleSetting` (PUT), `useResetModuleSetting` (DELETE),
 * `useValidateFormConfig` (debounced validate on changes).
 *
 * The Pane and PaneFooter pattern is preserved so the raw editor can
 * remain bit-identical when the parent ModeToggle swaps to raw. See
 * `FormConfigSettings.tsx` for the toggle host.
 */
import React, { useEffect, useLayoutEffect, useReducer, useRef, useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { Button, Pane, PaneFooter, Callout, ConfirmationModal, MessageBanner, Loading, } from '@folio/stripes/components';
import useModuleSetting, { moduleSettingInvalidationKeys } from '../../hooks/useModuleSetting';
import useSaveModuleSetting from '../../hooks/useSaveModuleSetting';
import useResetModuleSetting from '../../hooks/useResetModuleSetting';
import useValidateFormConfig from '../../hooks/useValidateFormConfig';
import usePackagedFormConfigDefault from '../../hooks/usePackagedFormConfigDefault';
import { editorReducer, initialEditorState } from './useEditorState';
import TabListEditor from './TabListEditor';
import FieldListEditor from './FieldListEditor';
import FieldPropertyPanel from './FieldPropertyPanel';
import { humanizeErrors } from './humanizeError';
import { readCsLabel } from './labelUtils';
import { sameContent } from '../formConfigHistory';
var FormConfigVisualEditor = function (_a) {
    var _b;
    var settingKey = _a.settingKey, header = _a.header, initialConfigOverride = _a.initialConfigOverride, onOverrideConsumed = _a.onOverrideConsumed, onSerialisedChange = _a.onSerialisedChange, onDirtyChange = _a.onDirtyChange;
    var intl = useIntl();
    var queryClient = useQueryClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var calloutRef = useRef(null);
    /** CZNA-493 — see `handleFieldMovedToTab`: where focus goes after a move. */
    var noFieldPlaceholderRef = useRef(null);
    var focusPlaceholderAfterMove = useRef(false);
    var _c = useModuleSetting(settingKey), data = _c.data, isLoading = _c.isLoading, isError = _c.isError, errorReason = _c.errorReason;
    var save = useSaveModuleSetting(settingKey);
    var reset = useResetModuleSetting(settingKey);
    // CZNA-494 / CZNA-535 — what a newly added field, and a newly added subfield
    // of an existing field, inherits. Advisory: a failure here just means both
    // add dialogs fall back to the MARC catalog.
    var packagedDefault = usePackagedFormConfigDefault(settingKey).packagedDefault;
    var _d = useReducer(editorReducer, initialEditorState), state = _d[0], dispatch = _d[1];
    var _e = useState(0), activeTabIdx = _e[0], setActiveTabIdx = _e[1];
    var _f = useState(null), selectedFieldIdx = _f[0], setSelectedFieldIdx = _f[1];
    var _g = useState(false), confirmResetOpen = _g[0], setConfirmResetOpen = _g[1];
    /**
     * Which field the user is looking at, by identity rather than by index.
     * Read by the reseed effect below to put them back on the same field after
     * the payload is replaced. Kept in a ref so the effect does not have to list
     * `state.config` / the two index states as dependencies (which would make it
     * re-run on every keystroke).
     */
    var selectedFieldRef = useRef(null);
    // Seed (and re-seed on data refresh) from server payload
    var lastSeededAt = useRef(null);
    useEffect(function () {
        if (!data)
            return;
        if (lastSeededAt.current === data.updatedAt && state.lastReseededAt === data.updatedAt)
            return;
        var nextConfig = data.value;
        dispatch({
            type: 'reseed',
            payload: {
                value: nextConfig,
                optimisticLock: data.optimisticLock,
                updatedAt: data.updatedAt,
            },
        });
        lastSeededAt.current = data.updatedAt;
        // Keep activeTabIdx if still in range; else clamp
        var nextTabIdx = Math.min(activeTabIdx, Math.max(0, nextConfig.tabs.length - 1));
        setActiveTabIdx(nextTabIdx);
        /**
         * CZNA-500 — re-point the selection at the SAME field instead of blanking
         * it. The reseed that follows a successful Save carries back exactly what
         * the admin just sent, so dropping the selection turned every save into
         * "the pane I was editing closed and nothing visibly changed" — the user
         * had no way to tell a save apart from a no-op, which is the same
         * can't-tell-if-it-worked trap that led NK to re-create a field instead of
         * editing it (CZNA-450 → CZNA-494).
         *
         * Matching on the tag, not the index: a reseed can also come from someone
         * else's save or from "Obnovit výchozí", where the field list really may
         * have shifted. If the field is gone, the selection is dropped — that is
         * the honest outcome, and it is now the exception rather than the rule.
         */
        var previous = selectedFieldRef.current;
        var nextTab = nextConfig.tabs[nextTabIdx];
        if (previous === null || !nextTab || previous.tabId !== nextTab.id) {
            setSelectedFieldIdx(null);
            return;
        }
        var nextIdx = nextTab.fields.findIndex(function (f) { return f.tag === previous.tag; });
        setSelectedFieldIdx(nextIdx === -1 ? null : nextIdx);
        // `activeTabIdx` is read, not tracked: adding it would re-run the whole
        // reseed on a plain tab click. The value read here is the one from the
        // render that scheduled this effect, which is what the clamp wants.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, state.lastReseededAt]);
    // Consume the one-shot override (TASK-181 review FIX 1). We seed the
    // reducer from the override after the server payload reseed has run
    // (so optimisticLock is populated), then notify the parent so it can
    // clear the prop. The override carries dirty user edits — the reducer
    // marks isDirty=true via the `seedFromOverride` action.
    var lastConsumedOverrideRef = useRef(null);
    useEffect(function () {
        if (!initialConfigOverride)
            return;
        if (lastConsumedOverrideRef.current === initialConfigOverride)
            return;
        // Wait until the server payload has seeded so optimisticLock is set.
        if (state.optimisticLock === null)
            return;
        dispatch({ type: 'seedFromOverride', value: initialConfigOverride });
        lastConsumedOverrideRef.current = initialConfigOverride;
        setSelectedFieldIdx(null);
        selectedFieldRef.current = null;
        setActiveTabIdx(function (idx) { return Math.min(idx, Math.max(0, initialConfigOverride.tabs.length - 1)); });
        if (onOverrideConsumed)
            onOverrideConsumed();
    }, [initialConfigOverride, state.optimisticLock, onOverrideConsumed]);
    // Debounced validate — serialize current config and pass JSON string
    var serialized = useMemo(function () { return (state.config.tabs.length > 0 ? JSON.stringify(state.config, null, 2) : ''); }, [state.config]);
    var validate = useValidateFormConfig(settingKey, serialized);
    // Humanize live validator findings. Computed here (above early-return
    // branches) so the hook order stays stable across loading/error/success
    // renders — React Rules of Hooks.
    //
    // CZNA-540: feed the summary EVERY finding, not only those from an invalid
    // result. `valid` is false iff something is BLOCKING, so gating on `!valid`
    // made the warnings section reachable only when a blocking error happened to
    // sit in the same payload — a warning on its own rendered nothing.
    var humanizedErrors = useMemo(function () { return humanizeErrors(validate.data ? validate.data.errors : [], state.config); }, [validate.data, state.config]);
    // Save toast (mirrors TASK-180 pattern)
    var wasSavingRef = useRef(false);
    useEffect(function () {
        var _a, _b, _c, _d;
        if (save.isLoading) {
            wasSavingRef.current = true;
            return;
        }
        if (wasSavingRef.current && !save.isError) {
            wasSavingRef.current = false;
            // CZNA-540 — see the sibling ModuleSettingEditor: a 200 can carry
            // advisory findings the save was let through with.
            var savedWarnings = (_c = (_b = (_a = save.data) === null || _a === void 0 ? void 0 : _a.warnings) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
            (_d = calloutRef.current) === null || _d === void 0 ? void 0 : _d.sendCallout(savedWarnings > 0
                ? {
                    type: 'warning',
                    message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.toast.savedWithWarnings' }, { count: savedWarnings }),
                }
                : {
                    type: 'success',
                    message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.toast.saved' }),
                });
        }
        if (wasSavingRef.current && save.isError) {
            wasSavingRef.current = false;
        }
    }, [save.isLoading, save.isError, save.data, intl]);
    var wasResettingRef = useRef(false);
    useEffect(function () {
        var _a;
        if (reset.isLoading) {
            wasResettingRef.current = true;
            return;
        }
        if (wasResettingRef.current && !reset.isError) {
            wasResettingRef.current = false;
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: 'success',
                message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.toast.resetDone' }),
            });
        }
        if (wasResettingRef.current && reset.isError) {
            wasResettingRef.current = false;
        }
    }, [reset.isLoading, reset.isError, intl]);
    /**
     * Single entry point for "the user picked a field". Keeps the index (what
     * the panel renders from) and the identity ref (what survives a reseed) from
     * drifting apart — see the reseed effect above.
     */
    var selectField = function (fieldIdx) {
        setSelectedFieldIdx(fieldIdx);
        var tab = state.config.tabs[activeTabIdx];
        var field = fieldIdx === null ? undefined : tab === null || tab === void 0 ? void 0 : tab.fields[fieldIdx];
        selectedFieldRef.current = field ? { tabId: tab === null || tab === void 0 ? void 0 : tab.id, tag: field.tag } : null;
    };
    /**
     * CZNA-493 — after a field has been moved to another tab.
     *
     * We deliberately STAY on the source tab and just drop the selection, rather
     * than following the field to its new home. Reorganising a form means moving
     * several fields in a row (NK's SUBJECT rework moved seven), and following
     * each one would force a manual switch back every time. The field vanishing
     * from the list is the visible confirmation; the toast names where it went,
     * so "stayed put" never reads as "nothing happened".
     */
    var handleFieldMovedToTab = function (toTabIdx) {
        var _a;
        setSelectedFieldIdx(null);
        selectedFieldRef.current = null;
        // Dropping the selection unmounts the whole property panel — the "⋯" menu
        // and its trigger with it — so a keyboard user who pressed Enter on a tab
        // name would be left on `document.body` with nothing to navigate from. The
        // placeholder that takes the panel's place is the honest anchor: it is
        // where their context went, and what it says ("pick a field on the left")
        // is exactly the next instruction. See the focus effect below.
        focusPlaceholderAfterMove.current = true;
        var target = state.config.tabs[toTabIdx];
        (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
            type: 'success',
            message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.property.moveToTab.toast' }, { tab: readCsLabel(target === null || target === void 0 ? void 0 : target.label) || (target === null || target === void 0 ? void 0 : target.id) || '' }),
        });
    };
    /**
     * Runs after the commit that swapped the panel for the placeholder, so the
     * node exists by the time we reach for it. A layout effect rather than a
     * passive one for the same reason as in `FieldPropertyPanel`: focus that has
     * to survive an unmount should not wait for a paint.
     */
    useLayoutEffect(function () {
        var _a;
        if (!focusPlaceholderAfterMove.current)
            return;
        // Disarmed after ONE commit, whether or not there was anything to focus.
        //
        // Blind review suggested holding the flag until the placeholder exists, so
        // a commit that renders the loading/error body instead does not burn the
        // shot. Declined, deliberately: that commit cannot happen here — the early
        // returns below are gated on `isLoading`, and react-query keeps `data`
        // across refetches, so `isLoading` is only true before the first load, i.e.
        // before any move can have been made. What holding the flag WOULD add is a
        // flag that can linger armed across arbitrarily many commits and then pull
        // focus off whatever the admin had opened — a reachable failure traded for
        // an unreachable one. It would also be untestable, and an untested guard is
        // not a guard.
        focusPlaceholderAfterMove.current = false;
        (_a = noFieldPlaceholderRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    });
    var handleSave = function () {
        if (state.optimisticLock === null)
            return;
        save.mutate({ value: state.config, optimisticLock: state.optimisticLock });
    };
    var handleReloadAfterConflict = function () {
        save.reset();
        moduleSettingInvalidationKeys(settingKey)
            .forEach(function (queryKey) { return queryClient.invalidateQueries(queryKey); });
    };
    var handleConfirmReset = function () {
        setConfirmResetOpen(false);
        reset.mutate();
    };
    // Stream the current serialised config up to the host so a visual→raw
    // toggle in the top toolbar can seed the raw editor with the live
    // edits. The host caches in a ref (no re-render storm).
    useEffect(function () {
        onSerialisedChange === null || onSerialisedChange === void 0 ? void 0 : onSerialisedChange(serialized);
    }, [serialized, onSerialisedChange]);
    // The reducer's `isDirty` says "something was dispatched since the last
    // reseed" — and every raw→visual switch dispatches an override, edited or
    // not. The restore warning needs "differs from the saved row", so a dirty
    // reducer only counts when its config really differs. The reducer's own flag
    // is left as it is. A clean reducer is equal to the row by construction, so
    // the (canonical, whole-document) comparison only runs once something was
    // dispatched — and only when the config or the row changes.
    var unsaved = useMemo(function () { return state.isDirty && data !== undefined && !sameContent(state.config, data.value); }, [state.isDirty, state.config, data]);
    useEffect(function () {
        onDirtyChange === null || onDirtyChange === void 0 ? void 0 : onDirtyChange(unsaved);
    }, [unsaved, onDirtyChange]);
    // ─── Render branches ─────────────────────────────────────────
    if (isLoading) {
        return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }) },
            React.createElement(Loading, null)));
    }
    if (isError && errorReason === 'forbidden') {
        return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }) },
            React.createElement("div", { "data-testid": "visual-editor-admin-only-placeholder" },
                React.createElement(MessageBanner, { type: "warning" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.placeholder.adminOnly" })))));
    }
    if (isError && errorReason === 'not-found') {
        return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }) },
            React.createElement("div", { "data-testid": "visual-editor-no-config-placeholder" },
                React.createElement(MessageBanner, { type: "warning" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.placeholder.noConfigYet" })))));
    }
    if (isError || !data) {
        return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }) },
            React.createElement("div", { "data-testid": "visual-editor-generic-error-placeholder" },
                React.createElement(MessageBanner, { type: "error" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.placeholder.genericError" })))));
    }
    var isConflict = save.errorStatus === 409;
    var resetUnavailable = reset.errorStatus === 409;
    // Inline per-property errors (FieldPropertyPanel / SubfieldEditor /
    // IndicatorEditor) render in red with no severity awareness, so they get
    // BLOCKING findings only — a warning shown as red inline text would read as
    // "fix this before you can save", which is the opposite of what it means.
    // Warnings reach the user through the amber summary section below.
    var liveErrors = validate.data
        ? validate.data.errors.filter(function (e) { return e.severity === 'BLOCKING'; })
        : [];
    // Warnings must NOT disable Save — the whole point of TASK-181 is that NK can
    // save a form referencing a custom in-house tag. `!valid` is already the right
    // gate (it is false only for BLOCKING); keep it off `errors.length`.
    var saveDisabled = save.isLoading || state.optimisticLock === null || (validate.data !== null && !validate.data.valid);
    var resetDisabled = reset.isLoading || (data.defaultVersion === null && !data.customized);
    var activeTab = state.config.tabs[activeTabIdx];
    var selectedField = activeTab && selectedFieldIdx !== null ? (_b = activeTab.fields[selectedFieldIdx]) !== null && _b !== void 0 ? _b : null : null;
    return (React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }), footer: (React.createElement(PaneFooter
        // Stripes PaneFooter gives `renderStart` flex:1 (greedy left)
        // and `renderEnd` natural width (right). Putting everything in
        // `renderEnd` left half the footer empty and forced the button
        // cluster to wrap. We split idiomatically: secondary actions
        // (import/export/reset/switch) on the left, primary Save on
        // the right.
        , { 
            // Stripes PaneFooter gives `renderStart` flex:1 (greedy left)
            // and `renderEnd` natural width (right). Putting everything in
            // `renderEnd` left half the footer empty and forced the button
            // cluster to wrap. We split idiomatically: secondary actions
            // (import/export/reset/switch) on the left, primary Save on
            // the right.
            renderStart: (React.createElement(React.Fragment, null,
                React.createElement(Button, { marginBottom0: true, onClick: function () { return setConfirmResetOpen(true); }, disabled: resetDisabled, "data-testid": "visual-editor-reset-btn" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.buttons.reset" })))), renderEnd: (React.createElement(Button, { marginBottom0: true, buttonStyle: "primary", onClick: handleSave, disabled: saveDisabled, "data-testid": "visual-editor-save-btn" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.buttons.save" }))) })) },
        header && (React.createElement("div", { style: { marginBottom: '0.75rem' }, "data-testid": "visual-editor-header-slot" }, header)),
        isConflict && (React.createElement(MessageBanner, { type: "error" },
            React.createElement("div", { "data-testid": "visual-editor-conflict-banner" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.banner.conflict" }),
                ' ',
                React.createElement(Button, { onClick: handleReloadAfterConflict, "data-testid": "visual-editor-conflict-reload-btn" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.banner.conflictReload" }))))),
        (save.timedOut || reset.timedOut) && (React.createElement(MessageBanner, { type: "error" },
            React.createElement("div", { "data-testid": "visual-editor-timeout-banner" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.error.timeout" })))),
        resetUnavailable && (React.createElement(MessageBanner, { type: "warning" },
            React.createElement("div", { "data-testid": "visual-editor-no-default-placeholder" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.placeholder.noDefaultAvailable" })))),
        React.createElement(TabListEditor, { tabs: state.config.tabs, activeTabIdx: activeTabIdx, onSelectTab: function (idx) { setActiveTabIdx(idx); selectField(null); }, dispatch: dispatch }),
        React.createElement("div", { style: { display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '1rem' }, "data-testid": "visual-editor-layout" },
            React.createElement("div", { style: { borderRight: '1px solid #eee', paddingRight: '0.5rem' } }, activeTab && (React.createElement(FieldListEditor, { tabIdx: activeTabIdx, tab: activeTab, selectedFieldIdx: selectedFieldIdx, onSelectField: selectField, dispatch: dispatch, config: state.config, packagedDefault: packagedDefault }))),
            React.createElement("div", { "data-testid": "visual-editor-right-pane" },
                selectedField && selectedFieldIdx !== null ? (React.createElement(FieldPropertyPanel, { tabIdx: activeTabIdx, fieldIdx: selectedFieldIdx, field: selectedField, validationErrors: liveErrors, dispatch: dispatch, tabs: state.config.tabs, onFieldMovedToTab: handleFieldMovedToTab, packagedDefault: packagedDefault })) : (React.createElement("div", { ref: noFieldPlaceholderRef, 
                    /* Focusable programmatically, but never in the tab order: it is a
                       landing spot after a move, not a stop on the way through.
                       `role="note"` and a name, because focus lands here: `aria-label`
                       and `aria-labelledby` are PROHIBITED on `role=generic`, so a
                       bare focusable div is a focus change with no announced context
                       — implementation-defined at best. */
                    role: "note", tabIndex: -1, "aria-labelledby": "visual-editor-no-field-placeholder-hint", "data-testid": "visual-editor-no-field-placeholder", style: { color: '#666', padding: '1rem' } },
                    React.createElement("p", { id: "visual-editor-no-field-placeholder-hint", style: { margin: 0 } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.selectFieldHint" })))),
                humanizedErrors.length > 0 && (React.createElement("div", { "data-testid": "visual-editor-errors-summary", style: { marginTop: '0.5rem' } },
                    humanizedErrors.some(function (e) { return e.severity === 'BLOCKING'; }) && (React.createElement(React.Fragment, null,
                        React.createElement("div", { style: { fontWeight: 'bold', color: '#c00' } },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.validate.errorsHeading" })),
                        humanizedErrors
                            .filter(function (e) { return e.severity === 'BLOCKING'; })
                            .map(function (err, i) { return (React.createElement("div", { key: "err-".concat(err.raw.path, "-").concat(i), style: { fontSize: '0.85rem', color: '#c00', padding: '0.25rem 0' }, "data-testid": "visual-editor-error-row", title: "".concat(err.raw.path, " \u2014 ").concat(err.raw.message) },
                            React.createElement("span", { style: { fontWeight: 600 } }, err.location),
                            ': ',
                            React.createElement("span", null, err.message))); }))),
                    humanizedErrors.some(function (e) { return e.severity === 'WARNING'; }) && (React.createElement("div", { style: { marginTop: humanizedErrors.some(function (e) { return e.severity === 'BLOCKING'; }) ? '0.5rem' : 0 } },
                        React.createElement("div", { style: { fontWeight: 'bold', color: '#a06000' } },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.validate.warningsHeading" })),
                        humanizedErrors
                            .filter(function (e) { return e.severity === 'WARNING'; })
                            .map(function (err, i) { return (React.createElement("div", { key: "warn-".concat(err.raw.path, "-").concat(i), style: { fontSize: '0.85rem', color: '#a06000', padding: '0.25rem 0' }, "data-testid": "visual-editor-warning-row", title: "".concat(err.raw.path, " \u2014 ").concat(err.raw.message) },
                            React.createElement("span", { style: { fontWeight: 600 } }, err.location),
                            ': ',
                            React.createElement("span", null, err.message))); }))))))),
        React.createElement(ConfirmationModal, { open: confirmResetOpen, heading: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.confirmReset.heading" }), message: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.confirmReset.message" }), confirmLabel: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.confirmReset.confirm" }), cancelLabel: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.confirmReset.cancel" }), onConfirm: handleConfirmReset, onCancel: function () { return setConfirmResetOpen(false); } }),
        React.createElement(Callout, { ref: calloutRef })));
};
export default FormConfigVisualEditor;
